import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowRightLeft,
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  History,
  ListChecks,
  OctagonAlert,
  Loader2,
  Moon,
  Paperclip,
  Scale,
  Search,
  Sun,
  X,
  Zap,
  UserCog,
  ArrowDown,
  ArrowUp,
} from 'lucide-react';

import PrivateLayout from '../../layouts/PrivateLayout';
import { cn } from '../../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { useAssignCm, useCmQueue, useRecentCmMoves, useSetTrainingMark } from '../../../services/cmAssignment';
import { THEME_KEY } from '../clientOnboarding/clientOnboardingUtils';
import { Picker } from '../clientOnboarding/ClientOnboardingSlideOver';
import { expediteLine } from '../clientOnboarding/ClientWorkspace';
import { HoverTip } from '../leads/teams/premium';

// CM Assignment (Nic 2026-10-09): every client who finished Client
// Onboarding (Ready for CM, an FOTC) waits here until the Onboarding Manager
// hands them to a Claims Manager. Assigning moves the claim to In Process,
// onto the CM's kanban, and the row falls off. The Caseloads rail shows each
// CM's open claims so the work stays even; a partner's routed CM comes
// prefilled. The queue pages on the server, 25 at a time (2026-10-13).
//
// 2026-10-26 (Nic): Expedited first (an Intent to File expiring within 90
// days, or marked by hand with a reason in their Workspace), then the longest wait, counted
// from the first time they were Waiting on PTRs. A second tab lists who is
// Waiting on PTRs, so the manager can assign one early when needed (their
// claim then waits on documents: Private treatment records).
//
// 2026-10-28 (Nic): a third tab, Waiting on one item: everyone whose
// checklist is down to one step (not PTRs alone), with what it is. A list to
// watch, never assignable from here: their specialist ticks Got it and they
// move to Ready for CM on their own.

const CARD =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';
const PAD_X = 'px-[16px] lg:px-[20px]';
// The rows and their column header share one grid (2026-10-28): checkbox,
// client, rating, specialist, wait, spread in proportion so nothing is
// pushed to the far edge.
// The last column (Nic 2026-11-02) is the Claims Manager preferences icon.
const GRID = 'md:grid-cols-[1.25rem_minmax(0,1.3fr)_minmax(0,0.7fr)_minmax(0,0.5fr)_minmax(0,1fr)_1.75rem]';
// How the list is sorted (Nic 2026-11-02): Client, Rating or Waiting, on
// the server (the queue pages there), remembered per viewer as "key:dir".
// Nothing saved is the default order (Expedited first, longest wait first).
const SORT_KEY = 'cmAssignment.sort';
const SORT_KEYS = ['name', 'rating', 'waiting'];
const readSort = () => {
  try {
    const [key, dir] = String(localStorage.getItem(SORT_KEY) || '').split(':');
    return SORT_KEYS.includes(key) ? { key, dir: dir === 'asc' ? 'asc' : 'desc' } : { key: null, dir: 'desc' };
  } catch {
    return { key: null, dir: 'desc' };
  }
};
// How the Caseloads rail is ordered (Nic 2026-10-13), remembered per viewer.
const CASELOAD_SORT_KEY = 'cmAssignment.caseloadSort';
const CASELOAD_SORTS = [
  { key: 'light', label: 'Lightest' },
  { key: 'heavy', label: 'Heaviest' },
  { key: 'name', label: 'A-Z' },
];
// The wait, as colored words with a sign (Nic 2026-10-30, no pill): green
// through 3 days, amber with a warning triangle from 4, red with a stop
// sign from 30.
const WAIT_WARN_DAYS = 3;
const WAIT_LATE_DAYS = 30;
const daysSince = (at) => (at ? Math.max(0, moment().startOf('day').diff(moment(at).startOf('day'), 'days')) : 0);

// Expedited on a row (Nic 2026-10-26): set in the client's Workspace, shown
// here with why. An Intent to File expiring within 90 days does it on its own.
// Expedited (Nic 2026-10-30): the word alone; the reason and who set it on
// hover.
const RowExpedite = ({ client }) => {
  const x = client.expedite;
  if (!x) return null;
  const why = [expediteLine(x), x.auto ? 'Automatic: their Intent to File expires within 90 days.' : x.byName ? `By ${x.byName}` : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <span
      title={why}
      aria-label={`Expedited: ${why}`}
      className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-px text-[11px] font-semibold text-rose-700 dark:text-rose-300"
    >
      <Zap size={11} /> Expedited
    </span>
  );
};

// Claims Manager preferences (Nic 2026-11-02): what Onboarding asked, as a
// premium icon at the right of the row like the Workspaces' nudge and notes
// icons, the answers on hover (and on a tap). Only rows with something to
// say get one: a requested CM, a woman or a man, a sensitive note.
export const prefLines = (p) =>
  [
    p?.requestedCm ? `Asked for ${p.requestedCm}` : p?.requestedCmUserId ? 'Asked for a Claims Manager no longer on the rotation' : null,
    p?.genderLabel || null,
    p?.note ? `Sensitive: ${p.note}` : null,
  ].filter(Boolean);
const CmPrefsMark = ({ client }) => {
  const p = client.cmPreferences;
  if (!p) return null;
  const lines = prefLines(p);
  const asked = p.askedBy ? `Asked by ${p.askedBy}${p.askedAt ? `, ${moment(p.askedAt).format('MMM D')}` : ''}` : null;
  return (
    <HoverTip
      wide
      align="right"
      label={
        <span className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-violet-300">Claims Manager preferences</span>
          {lines.map((l) => (
            <span key={l} className="mt-1 block">
              {l}
            </span>
          ))}
          {asked ? <span className="mt-1.5 block text-[10px] text-slate-400">{asked}</span> : null}
        </span>
      }
    >
      <span
        aria-label={`Claims Manager preferences: ${lines.join('. ')}`}
        className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50 text-violet-600 shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-violet-200/80 transition-transform duration-150 hover:-translate-y-px hover:bg-violet-100 dark:!bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/25 dark:hover:!bg-violet-400/20"
      >
        <UserCog size={14} strokeWidth={2.25} />
      </span>
    </HoverTip>
  );
};

// A sortable column head for the grid (the Onboarding Workspace's SortHead,
// as a div): a click sorts descending, another ascending, a third goes back
// to the default order.
const GridSortHead = ({ sortKey, label, sort, setSort }) => {
  const on = sort.key === sortKey;
  const Icon = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  const next = on ? (sort.dir === 'desc' ? { key: sortKey, dir: 'asc' } : { key: null, dir: 'desc' }) : { key: sortKey, dir: 'desc' };
  return (
    <div role="columnheader" aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => setSort(next)}
        title={on && sort.dir === 'asc' ? 'Back to the usual order' : `Sort by ${label.toLowerCase()}`}
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 text-[10.5px] font-bold uppercase tracking-wider outline-none focus:outline-none',
          on ? 'text-sky-700 dark:text-sky-300' : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200',
        )}
      >
        {label}
        {on ? <Icon size={11} /> : null}
      </button>
    </div>
  );
};

const readTheme = () => {
  try {
    return localStorage.getItem(THEME_KEY) || 'light';
  } catch {
    return 'light';
  }
};

const openClaim = (claimId) => window.open(`/admin/profile/${claimId}`, '_blank', 'noopener');

// The last 7 days of moves, in a pop-out under its button. The shared
// popover (Nic 2026-10-27): it was hand-placed to the button's right edge
// and ran off a narrow screen; this one slides to stay inside the window.
const RecentMoves = () => {
  const [open, setOpen] = useState(false);
  const q = useRecentCmMoves(open);
  const moves = q.data?.moves || [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
          'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[12px] font-medium outline-none focus:outline-none',
          open
            ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
            : 'bg-white text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/80 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10',
        )}
      >
          <History size={14} /> Moved to CM
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Moved to a CM, last 7 days"
        className="w-[460px] overflow-hidden rounded-2xl p-0 shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-slate-200"
      >
          <div className="flex items-center gap-2 border-b border-slate-100 px-[16px] py-3 dark:border-white/10">
            <History size={15} className="shrink-0 text-slate-500 dark:text-slate-400" />
            <div className="min-w-0 flex-1 text-sm font-bold text-slate-800 dark:text-slate-100">
              Moved to a CM, last 7 days
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-white"
            >
              <X size={15} />
            </button>
          </div>
          <div className="os-scroll max-h-[440px] overflow-y-auto overflow-x-hidden">
            {q.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 size={16} className="animate-spin text-slate-400" />
              </div>
            ) : moves.length === 0 ? (
              <div className="px-[16px] py-8 text-center text-sm text-slate-400">Nobody moved this week.</div>
            ) : (
              <ul className="mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/10">
                {moves.map((m, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => openClaim(m.claimId)}
                      className="flex w-full items-center gap-3 px-[16px] py-2.5 text-left outline-none hover:bg-slate-50 focus:outline-none dark:hover:!bg-white/[0.04]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {m.name}
                        </div>
                        <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                          to{' '}
                          <span className="font-semibold text-slate-700 dark:text-slate-200">
                            {m.claimsManager || 'a CM'}
                          </span>
                          {m.by ? ` · by ${m.by}` : ''}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs tabular-nums text-slate-400">
                        {moment(m.at).format('ddd MMM D, h:mm A')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
      </PopoverContent>
    </Popover>
  );
};

// One client: who, how long they've waited, and the pick + Assign. Assign
// asks once, in place ("Assign to Laura?"), then commits.
// One row (Nic 2026-10-28: say more by showing less). Left to right: a
// checkbox (Ready for CM and Waiting on PTRs only), the name with only what
// changes a decision under it (the partner, the partner's CM pick, Expedited),
// the rating as its own figure, the specialist as a circle of initials
// (full name on hover), then the wait. No picker and no Assign on the row:
// tick people and the bar above the list assigns them together.
//   early     the Waiting on PTRs tab (the pill reads On PTRs; a paperclip
//             with the count only when files have arrived)
//   readOnly  the Waiting on one item tab (the one step in place of the wait,
//             no checkbox)
const ClientRow = ({ client, cms, early = false, readOnly = false, selected = false, assigning = false, onToggle }) => {
  const waited = daysSince(client.waitSince);
  const waitPill = readOnly ? (
    <span
      className="inline-flex max-w-full items-center whitespace-nowrap rounded-full bg-violet-500/10 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:text-violet-300"
      title={client.waitingOn?.label}
    >
      {client.waitingOn?.label || 'Waiting on one item'}
    </span>
  ) : (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold tabular-nums',
        waited >= WAIT_LATE_DAYS
          ? 'text-rose-600 dark:text-rose-300'
          : waited > WAIT_WARN_DAYS
            ? 'text-amber-600 dark:text-amber-300'
            : 'text-emerald-600 dark:text-emerald-300',
      )}
      title={[
        client.ptrsWaitingAt ? `Waiting on PTRs since ${moment(client.ptrsWaitingAt).format('MMM D')}` : null,
        client.readyAt ? `Ready since ${moment(client.readyAt).format('MMM D, h:mm A')}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
    >
      {waited >= WAIT_LATE_DAYS ? (
        <OctagonAlert size={13} strokeWidth={2.25} className="shrink-0" aria-label="Waiting 30 days or more" />
      ) : waited > WAIT_WARN_DAYS ? (
        <AlertTriangle size={13} strokeWidth={2.25} className="shrink-0" aria-label="Waiting more than 3 days" />
      ) : null}
      {waited === 0
        ? early
          ? 'On PTRs since today'
          : 'Ready today'
        : `${early ? 'On PTRs' : 'Waiting'} ${waited} day${waited === 1 ? '' : 's'}`}
    </span>
  );
  // Files that have arrived for their PTRs: worth a glyph; none is not.
  const files =
    early && client.ptrUploads ? (
      <span
        title={`${client.ptrUploads} PTR file${client.ptrUploads === 1 ? '' : 's'} sent`}
        className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-sky-600 dark:text-sky-300"
      >
        <Paperclip size={11} /> {client.ptrUploads}
      </span>
    ) : null;
  const partnerPick = client.suggestedCmUserId ? cms.find((c) => c.userId === client.suggestedCmUserId)?.name : null;
  // Their current rating, the figure alone (Nic 2026-10-28: no "now").
  const rating = (
    <span title="Current VA rating" className="whitespace-nowrap text-[15px] font-bold tabular-nums text-slate-800 dark:text-slate-100">
      {client.rating != null ? `${client.rating}%` : 'None'}
    </span>
  );
  const specialist = client.specialist ? (
    <span
      title={`With ${client.specialist}`}
      aria-label={`With ${client.specialist}`}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 dark:!bg-white/10 dark:text-slate-300"
    >
      {initialsOf(client.specialist)}
    </span>
  ) : null;
  return (
    // Below md: the checkbox, then name, rating and specialist on one line
    // with the wait under it; from md each has its column.
    <li
      className={cn(
        'grid grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 py-3',
        GRID,
        PAD_X,
        selected && 'bg-sky-50/60 dark:!bg-sky-500/[0.06]',
        // On its way to a Claims Manager: dimmed, no more clicks.
        assigning && 'pointer-events-none opacity-50',
      )}
      aria-busy={assigning || undefined}
    >
      <div className="flex items-center">
        {readOnly ? null : assigning ? (
          <Loader2 size={15} className="animate-spin text-slate-400" aria-label="Assigning" />
        ) : (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={`Select ${client.name}`}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-sky-600 dark:border-white/20"
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          {client.claimId ? (
            // The name opens their claim in a new tab (Nic 2026-10-29). No
            // underline: the hover tint is the only cue.
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openClaim(client.claimId);
              }}
              title="Open their claim in a new tab"
              className="min-w-0 truncate border-0 bg-transparent p-0 text-left text-sm font-semibold text-slate-800 outline-none hover:text-sky-700 focus:outline-none dark:text-slate-100 dark:hover:text-sky-300"
            >
              {client.name}
            </button>
          ) : (
            <span className="min-w-0 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{client.name}</span>
          )}
          {/* Carried over at launch (Nic 2026-10-30): a quiet mark after the
              name, the story on hover. */}
          {client.carriedOver ? (
            // A title on the svg itself shows no tooltip; the span carries it.
            <span
              title="Carried over at launch: their claim was already In Process, so they came straight here without the new checklist."
              aria-label="Carried over at launch"
              className="inline-flex shrink-0 text-amber-600 dark:text-amber-300"
            >
              <ArrowLeftRight size={14} strokeWidth={2} />
            </span>
          ) : null}
          <span className="md:hidden">{rating}</span>
          <span className="md:hidden">{specialist}</span>
        </div>
        {/* One wrapping line under the name: on a phone the wait pill leads
            it (Nic 2026-10-28: Carried over and Ready today on one line). */}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="md:hidden">{waitPill}</span>
            {files ? <span className="md:hidden">{files}</span> : null}
            {/* Where they came from (Nic 2026-10-30): one label, the detail on hover. */}
            {client.source ? (
              <span title={client.source.detail} className="rounded bg-slate-100 px-1.5 py-px font-semibold dark:!bg-white/10">
                {client.source.label}
              </span>
            ) : null}
            <RowExpedite client={client} />
            {partnerPick ? (
              <span title="The Claims Manager their partner routes to" className="font-semibold text-sky-700 dark:text-sky-300">
                Partner pick: {partnerPick}
              </span>
            ) : null}
            {client.cmPreferences ? (
              <span className="md:hidden">
                <CmPrefsMark client={client} />
              </span>
            ) : null}
        </div>
      </div>
      <div className="hidden md:block">{rating}</div>
      <div className="hidden md:block">{specialist}</div>
      <div className="hidden min-w-0 items-center gap-2 md:flex">
        {waitPill}
        {files}
      </div>
      <div className="hidden md:flex md:justify-center">
        <CmPrefsMark client={client} />
      </div>
    </li>
  );
};

// The bar above the list once someone is ticked (Nic 2026-10-28): one
// picker, one Assign, one confirmation for everyone selected. Each person
// still goes through the same assign call and the same rechecks; a refusal
// skips that one and the toast says who.
// secondary (2026-10-30): a quiet second action on the ticked rows (For
// training on Ready, Back to Ready on For Training). placeholder and empty
// let the For Training tab say its picker lists trainees only.
const BulkAssignBar = ({
  count,
  cms,
  pick,
  onPick,
  onAssign,
  assigning,
  onClear,
  secondary = null,
  placeholder = 'Pick a Claims Manager',
  empty = null,
}) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => setArmed(false), [pick, count]);
  const cmName = cms.find((c) => c.userId === pick)?.name || '';
  return (
    <div
      role="region"
      aria-label="Assign the selected clients"
      // Always there, quiet, and inert until someone is ticked (Nic
      // 2026-10-28: no popping in and out).
      // A quiet strip (Nic 2026-10-28: a dark one was tried and was too much).
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-100 bg-slate-50/80 py-1.5 dark:border-white/10 dark:!bg-white/[0.03]',
        PAD_X,
      )}
    >
      <span className={cn('text-[13px] font-bold tabular-nums', count ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500')}>
        {count} selected
      </span>
      {count ? (
        <button
          type="button"
          onClick={onClear}
          className="text-[11.5px] font-semibold text-slate-500 outline-none hover:underline focus:outline-none dark:text-slate-400"
        >
          Clear
        </button>
      ) : null}
      {secondary ? (
        <button
          type="button"
          disabled={!count || secondary.busy}
          onClick={secondary.onClick}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-semibold text-sky-700 outline-none ring-1 ring-sky-200 hover:bg-sky-50 focus:outline-none disabled:opacity-40 dark:text-sky-300 dark:ring-sky-400/30 dark:hover:!bg-sky-500/10"
        >
          {secondary.busy ? <Loader2 size={13} className="animate-spin" /> : <secondary.icon size={13} />}
          {secondary.label}
        </button>
      ) : null}
      {empty && !cms.length ? (
        <span className="text-[12px] text-slate-500 dark:text-slate-400">{empty}</span>
      ) : null}
      <div className="min-w-0 flex-1 sm:max-w-xs">
        <Picker
          value={pick || ''}
          disabled={!count || !cms.length}
          placeholder={placeholder}
          // Lightest first by weighted load, shown beside each name (2026-10-13).
          options={[...cms]
            .sort((x, y) => x.weighted - y.weighted || x.name.localeCompare(y.name))
            .map((c) => ({
              value: c.userId,
              label: c.name,
              hint: Number.isInteger(c.weighted) ? String(c.weighted) : c.weighted.toFixed(1),
            }))}
          onPick={(v) => onPick(v || null)}
        />
      </div>
      {/* Assign is a fixed 92px; the confirmation is two squares in the same
          92px (a green check, a red x), so nothing shifts (Nic 2026-10-28). */}
      <div className="ml-auto flex w-[92px] shrink-0 items-center justify-end">
        {armed ? (
          <div className="flex w-full overflow-hidden rounded-lg ring-1 ring-slate-200 dark:ring-white/15">
            <button
              type="button"
              disabled={assigning}
              onClick={onAssign}
              aria-label={`Yes, assign ${count} to ${cmName}`}
              title={`Assign ${count} to ${cmName}`}
              className="flex h-8 flex-1 items-center justify-center bg-emerald-500/15 text-emerald-700 outline-none hover:bg-emerald-500 hover:text-white focus:outline-none disabled:opacity-50 dark:text-emerald-300"
            >
              {assigning ? <Loader2 size={14} className="animate-spin" /> : <Check size={16} strokeWidth={2.5} />}
            </button>
            <button
              type="button"
              aria-label="Cancel"
              title="Cancel"
              onClick={() => setArmed(false)}
              className="flex h-8 flex-1 items-center justify-center bg-rose-500/10 text-rose-600 outline-none hover:bg-rose-500 hover:text-white focus:outline-none dark:text-rose-300"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={!pick || !count}
            onClick={() => setArmed(true)}
            className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-slate-900 text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900"
          >
            <ArrowRightLeft size={14} /> Assign
          </button>
        )}
      </div>
    </div>
  );
};

const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';

const byName = (a, b) => a.name.localeCompare(b.name);

// The statuses a CM carries, in My Performance's Queue Load colors. Submitted
// counts half in the weighted measure (server/lib/queueLoad.js).
const STATUS_ROWS = [
  {
    key: 'in process',
    label: 'In Process',
    bar: 'bg-blue-500',
    chip: 'bg-blue-50 text-blue-700 dark:!bg-blue-500/10 dark:text-blue-300',
  },
  {
    key: 'awaiting internal review',
    label: 'Awaiting Review',
    bar: 'bg-amber-400',
    chip: 'bg-amber-50 text-amber-700 dark:!bg-amber-500/10 dark:text-amber-300',
  },
  {
    key: 'internal review approved',
    label: 'Review Approved',
    bar: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 dark:!bg-emerald-500/10 dark:text-emerald-300',
  },
  {
    key: 'submitted to va',
    label: 'Submitted',
    weight: 0.5,
    bar: 'bg-violet-400',
    chip: 'bg-violet-50 text-violet-700 dark:!bg-violet-500/10 dark:text-violet-300',
  },
];

// What the rail measures by (Nic 2026-10-13). Each picks a number per CM and
// the bar segments that make it up.
const MEASURES = [
  {
    key: 'weighted',
    label: 'Weighted',
    blurb: 'Open claims, Submitted counting half, as on My Performance.',
    value: (cm) => cm.weighted,
    segments: (cm) => STATUS_ROWS.map((s) => ({ ...s, n: (cm.byStatus?.[s.key] || 0) * (s.weight || 1) })),
  },
  {
    key: 'veterans',
    label: 'Veterans',
    blurb: 'Every open claim, In Process to Submitted to VA, counted once.',
    value: (cm) => cm.carrying,
    segments: (cm) => STATUS_ROWS.map((s) => ({ ...s, n: cm.byStatus?.[s.key] || 0 })),
  },
  {
    key: 'presub',
    label: 'Pre-sub',
    blurb: 'Claims still needing their work: In Process to Review Approved.',
    value: (cm) => cm.preSubmission,
    segments: (cm) => STATUS_ROWS.filter((s) => !s.weight).map((s) => ({ ...s, n: cm.byStatus?.[s.key] || 0 })),
  },
  {
    key: 'recent',
    label: 'Given 7d',
    blurb: 'Clients handed to them from this page in the last 7 days.',
    value: (cm) => cm.lastSevenDays,
    segments: (cm) => [{ key: 'recent', label: 'Given', bar: 'bg-indigo-400', n: cm.lastSevenDays }],
  },
];
const MEASURE_KEY = 'cmAssignment.caseloadMeasure';

const readPref = (key, allowed, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
};
const savePref = (key, v) => {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* private mode: this session only */
  }
};

const sortCms = (cms, by, valueOf) =>
  [...cms].sort((a, b) => {
    if (by === 'name') return byName(a, b);
    const diff = by === 'heavy' ? valueOf(b) - valueOf(a) : valueOf(a) - valueOf(b);
    return diff || byName(a, b);
  });

const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

// Against the team average: room (green), about even, heavy (amber).
const toneOf = (v, avg) => {
  if (!avg || v < avg * 0.75) return 'text-emerald-700 dark:text-emerald-300';
  if (v <= avg * 1.25) return 'text-slate-800 dark:text-slate-100';
  return 'text-amber-700 dark:text-amber-300';
};

// One CM opened up: their Queue Load card, as on My Performance.
const CaseloadDetail = ({ cm }) => (
  <div className="space-y-2.5 px-[16px] pb-3 pt-1">
    <div className="flex items-baseline gap-4 rounded-lg bg-slate-50 px-3 py-2 dark:!bg-white/[0.04]">
      <div>
        <span className="text-lg font-bold tabular-nums text-slate-800 dark:text-slate-100">{cm.carrying}</span>
        <span className="ml-1 text-[11px] text-slate-500 dark:text-slate-400">veterans</span>
      </div>
      <div>
        <span className="text-lg font-bold tabular-nums text-slate-500 dark:text-slate-300">{fmt(cm.weighted)}</span>
        <span className="ml-1 text-[11px] text-slate-500 dark:text-slate-400">weighted</span>
      </div>
    </div>
    <ul className="mb-0 list-none space-y-1 pl-0">
      {STATUS_ROWS.map((st) => (
        <li
          key={st.key}
          className={cn('flex items-center justify-between rounded-md px-2.5 py-1 text-[12px] font-medium', st.chip)}
        >
          <span>
            {st.label}
            {st.weight ? <span className="ml-1 opacity-60">x{st.weight}</span> : null}
          </span>
          <span className="font-bold tabular-nums">{cm.byStatus?.[st.key] || 0}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Segmented = ({ label, options, value, onChange, className }) => (
  <div
    role="group"
    aria-label={label}
    className={cn('flex shrink-0 items-center rounded-full bg-slate-100 p-0.5 dark:!bg-white/10', className)}
  >
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        aria-pressed={value === o.key}
        onClick={() => onChange(o.key)}
        className={cn(
          'h-6 flex-1 whitespace-nowrap rounded-full px-2 text-[11px] font-semibold outline-none focus:outline-none',
          value === o.key
            ? 'bg-white text-slate-800 shadow-sm dark:!bg-slate-100 dark:!text-slate-900'
            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Caseloads (was "CM load", Nic 2026-10-13): each Claims Manager's load as a
// bar split by status, against the team average, measured weighted (My
// Performance's Queue Load), by veterans, pre-submission, or given lately,
// and sorted lightest or heaviest first. A CM opens to their breakdown.
// Assigning is only the row's picker (click-to-pick went, Nic 2026-10-13).
// trainingOnly (2026-10-30): the For Training tab hands in the Claims
// Managers In Training only, so the average and Most room compare them.
const CaseloadRail = ({ cms, trainingOnly = false }) => {
  const [sort, setSort] = useState(() =>
    readPref(
      CASELOAD_SORT_KEY,
      CASELOAD_SORTS.map((s) => s.key),
      'light',
    ),
  );
  const [measureKey, setMeasureKey] = useState(() =>
    readPref(
      MEASURE_KEY,
      MEASURES.map((m) => m.key),
      'weighted',
    ),
  );
  const [openId, setOpenId] = useState(null);
  const measure = MEASURES.find((m) => m.key === measureKey) || MEASURES[0];
  const sorted = useMemo(() => sortCms(cms, sort, measure.value), [cms, sort, measure]);
  const values = cms.map(measure.value);
  const max = Math.max(1, ...values);
  const avg = cms.length ? values.reduce((n, v) => n + v, 0) / cms.length : 0;
  const least = values.length ? Math.min(...values) : null;
  // "Most room": the one lightest CM, when there is one.
  const roomiest = values.filter((v) => v === least).length === 1 ? cms[values.indexOf(least)]?.userId : null;
  const avgAt = `${Math.min(100, (avg / max) * 100)}%`;
  const legend =
    measure.key === 'recent' ? [] : measure.key === 'presub' ? STATUS_ROWS.filter((s) => !s.weight) : STATUS_ROWS;

  return (
    <aside
      className={cn(CARD, 'flex h-fit min-h-0 flex-col overflow-hidden lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]')}
    >
      <div className="space-y-2 border-b border-slate-100 px-[16px] pb-3 pt-3 dark:border-white/10">
        <div className="flex items-center gap-2">
          <Scale size={15} className="shrink-0 text-indigo-500" />
          <span className="flex-1 text-sm font-bold text-slate-800 dark:text-slate-100">
            {trainingOnly ? 'Caseloads, in training' : 'Caseloads'}
          </span>
          <Segmented
            label="Order"
            options={CASELOAD_SORTS}
            value={sort}
            onChange={(k) => {
              setSort(k);
              savePref(CASELOAD_SORT_KEY, k);
            }}
          />
        </div>
        <Segmented
          label="Measure by"
          options={MEASURES}
          value={measure.key}
          onChange={(k) => {
            setMeasureKey(k);
            savePref(MEASURE_KEY, k);
          }}
          className="w-full"
        />
        <div className="text-[11px] leading-snug text-slate-400 dark:text-slate-500">
          {measure.blurb} Team average{' '}
          <span className="font-semibold tabular-nums text-slate-600 dark:text-slate-300">
            {fmt(Math.round(avg * 10) / 10)}
          </span>
          .
        </div>
      </div>

      <ul className="os-scroll mb-0 min-h-0 flex-1 list-none overflow-y-auto pb-2 pl-0">
        {sorted.map((cm) => {
          const v = measure.value(cm);
          const open = openId === cm.userId;
          const toggle = () => setOpenId(open ? null : cm.userId);
          return (
            <li key={cm.userId} className={cn(open && 'bg-slate-50/60 dark:!bg-white/[0.02]')}>
              <button
                type="button"
                onClick={toggle}
                aria-expanded={open}
                title={open ? 'Hide their breakdown' : 'Show their breakdown'}
                className="flex w-full items-center gap-3 py-2 pl-[16px] pr-2 text-left outline-none hover:bg-slate-50 focus:outline-none dark:hover:!bg-white/[0.04]"
              >
                <span
                  aria-hidden
                  className="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600 dark:!bg-white/10 dark:text-slate-300"
                >
                  {initialsOf(cm.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      <span className="truncate text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                        {cm.name}
                      </span>
                      {roomiest === cm.userId && sort !== 'name' ? (
                        <span className="shrink-0 rounded-full bg-emerald-500/10 px-1.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                          Most room
                        </span>
                      ) : null}
                      {cm.inTraining && !trainingOnly ? (
                        <span
                          title="Marked In Training on Internal Users"
                          className="shrink-0 rounded-full bg-sky-500/10 px-1.5 text-[10px] font-semibold text-sky-700 dark:text-sky-300"
                        >
                          Training
                        </span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-baseline gap-1.5">
                      {cm.lastSevenDays && measure.key !== 'recent' ? (
                        <span
                          title={`Given ${cm.lastSevenDays} in the last 7 days`}
                          className="rounded-full bg-indigo-500/10 px-1.5 text-[10.5px] font-semibold tabular-nums text-indigo-700 dark:text-indigo-300"
                        >
                          +{cm.lastSevenDays}
                        </span>
                      ) : null}
                      <span className={cn('text-[15px] font-bold leading-none tabular-nums', toneOf(v, avg))}>
                        {fmt(v)}
                      </span>
                    </span>
                  </div>
                  <div className="relative mt-1.5 h-1.5 rounded-full bg-slate-100 dark:!bg-white/10">
                    <div className="flex h-full overflow-hidden rounded-full" style={{ width: `${(v / max) * 100}%` }}>
                      {measure
                        .segments(cm)
                        .map((s) =>
                          s.n > 0 ? (
                            <span
                              key={s.key}
                              title={`${s.label}: ${fmt(s.n)}`}
                              className={cn('h-full', s.bar)}
                              style={{ flexGrow: s.n, flexBasis: 0 }}
                            />
                          ) : null,
                        )}
                    </div>
                    {avg > 0 ? (
                      <span
                        aria-hidden
                        title="Team average"
                        className="absolute -top-0.5 h-2.5 w-px bg-slate-500/70 dark:bg-slate-300/60"
                        style={{ left: avgAt }}
                      />
                    ) : null}
                  </div>
                </div>
                <ChevronDown
                  size={14}
                  aria-hidden
                  className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
                />
              </button>
              {open ? <CaseloadDetail cm={cm} /> : null}
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 px-[16px] py-2 text-[10.5px] text-slate-400 dark:border-white/10 dark:text-slate-500">
        {legend.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className={cn('size-1.5 rounded-full', s.bar)} /> {s.label}
          </span>
        ))}
        <span className="ml-auto inline-flex items-center gap-1">
          <span className="h-2.5 w-px bg-slate-500/70" /> Average
        </span>
      </div>
    </aside>
  );
};

const PagerButton = ({ dir, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-label={dir === 'prev' ? 'Previous page' : 'Next page'}
    className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
  >
    {dir === 'prev' ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
  </button>
);

const CmAssignmentPage = () => {
  const [theme, setTheme] = useState(readTheme);
  const [page, setPage] = useState(1);
  // Ready for CM, or Waiting on PTRs (assign early when needed, 2026-10-26).
  const [tab, setTab] = useState('ready');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);
  // The column sort (Nic 2026-11-02), remembered per viewer.
  const [sort, setSort] = useState(readSort);
  useEffect(() => savePref(SORT_KEY, sort.key ? `${sort.key}:${sort.dir}` : ''), [sort]);
  // A new search, tab or sort starts at page 1.
  useEffect(() => setPage(1), [q, tab, sort.key, sort.dir]);

  const list = useCmQueue({ page, q, tab, sort: sort.key, dir: sort.dir });
  const ptrs = list.data?.ptrs || { waiting: 0, expedited: 0 };
  const one = list.data?.one || { waiting: 0, expedited: 0 };
  // For Training (2026-10-30): Ready clients reserved for a Claims Manager
  // In Training.
  const training = list.data?.training || { waiting: 0 };
  const clients = list.data?.clients || [];
  const cms = useMemo(() => list.data?.claimsManagers || [], [list.data?.claimsManagers]);
  // Who the picker and the rail show (Nic 2026-11-03): trainees only on the
  // Training tab, never on Ready or PTRs; everyone else only off it.
  const pickable = useMemo(() => cms.filter((c) => (tab === 'training' ? c.inTraining : !c.inTraining)), [cms, tab]);
  const total = list.data?.total || 0;
  const waiting = list.data?.waiting ?? total;
  const pageSize = list.data?.pageSize || 25;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);
  // Assigning the last row of the last page: step back a page.
  useEffect(() => {
    if (list.data && page > pages) setPage(pages);
  }, [list.data, page, pages]);
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(pages, p + 1));

  // Who is ticked (kept across pages, cleared on a tab change), and the one
  // CM picked for all of them (Nic 2026-10-28).
  const [selected, setSelected] = useState(() => new Set());
  const [pick, setPick] = useState(null);
  // A tab change clears the ticks and the picked Claims Manager (QA
  // 2026-10-30): the For Training picker lists only trainees, so a pick
  // carried over could be one it never shows.
  useEffect(() => {
    setSelected(new Set());
    setPick(null);
  }, [tab]);
  const toggle = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Claims Manager preferences (Nic 2026-11-02): with nobody picked yet, the
  // picker pre-selects the Claims Manager every ticked row asked for, when
  // they all asked for the same one and that CM is pickable here. The
  // manager can still change it.
  // A pick made here (not by hand) follows the ticks: it clears when the
  // ticked rows stop agreeing, and comes back when they agree again
  // (review 2026-11-03). A pick made by hand is never touched.
  const autoPick = useRef(null);
  useEffect(() => {
    const wanted = [...selected].map((id) => clients.find((c) => c.onboardingId === id)?.cmPreferences?.requestedCmUserId || null);
    const want = selected.size && wanted[0] && wanted.every((w) => w === wanted[0]) && pickable.some((c) => c.userId === wanted[0]) ? wanted[0] : null;
    if (pick && pick !== autoPick.current) return;
    if (want !== pick) {
      autoPick.current = want;
      setPick(want);
    }
    // Only a new tick can change the answer; the pick itself never re-runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, clients, pickable]);
  const assign = useAssignCm({ quiet: true });
  const queryClient = useQueryClient();
  // The rows being assigned right now: dimmed, with a spinner where the
  // checkbox was, until all are done.
  const [assigningIds, setAssigningIds] = useState(() => new Set());
  // Each one goes through the same call and the same rechecks, all at
  // once; then the list refreshes ONCE so the rows leave together (Nic
  // 2026-10-28: they were dropping out one by one). A refusal keeps that
  // person on the list, still ticked, and its own toast says who.
  const assignSelected = async () => {
    const ids = [...selected];
    const cmName = cms.find((c) => c.userId === pick)?.name || 'their Claims Manager';
    setAssigningIds(new Set(ids));
    const results = await Promise.allSettled(ids.map((id) => assign.mutateAsync({ onboardingId: id, claimsManagerUserId: pick })));
    const done = ids.filter((_, i) => results[i].status === 'fulfilled');
    const failed = ids
      .map((id, i) => (results[i].status === 'rejected' ? { id, e: results[i].reason } : null))
      .filter(Boolean)
      .map(({ id, e }) => `${clients.find((c) => c.onboardingId === id)?.name || 'One client'}: ${e?.response?.data?.msg || "didn't go through"}`);
    // Gone together: drop them from the cached page first, then refresh.
    if (done.length) {
      const gone = new Set(done);
      queryClient.setQueriesData(['cm-assignment', 'queue'], (data) =>
        data ? { ...data, clients: (data.clients || []).filter((c) => !gone.has(c.onboardingId)) } : data,
      );
      setSelected((s) => new Set([...s].filter((id) => !gone.has(id))));
    }
    setAssigningIds(new Set());
    queryClient.invalidateQueries(['cm-assignment']);
    queryClient.invalidateQueries(['client-onboarding']);
    if (done.length) {
      toast.success(
        `${done.length === 1 ? '1 client is' : `${done.length} clients are`} with ${cmName}. ${done.length === 1 ? 'Their claim is' : 'Their claims are'} In Process.`,
      );
    }
    failed.forEach((f) => toast.error(f));
  };

  // For training / Back to Ready (2026-10-30): the same shape as Assign, for
  // everyone ticked, one refresh when all are done.
  const mark = useSetTrainingMark({ quiet: true });
  const markSelected = async (on) => {
    const ids = [...selected];
    setAssigningIds(new Set(ids));
    const results = await Promise.allSettled(ids.map((id) => mark.mutateAsync({ onboardingId: id, on })));
    const done = ids.filter((_, i) => results[i].status === 'fulfilled');
    const failed = ids
      .map((id, i) => (results[i].status === 'rejected' ? { id, e: results[i].reason } : null))
      .filter(Boolean)
      .map(({ id, e }) => `${clients.find((c) => c.onboardingId === id)?.name || 'One client'}: ${e?.response?.data?.msg || "didn't go through"}`);
    if (done.length) {
      const gone = new Set(done);
      queryClient.setQueriesData(['cm-assignment', 'queue'], (data) =>
        data ? { ...data, clients: (data.clients || []).filter((c) => !gone.has(c.onboardingId)) } : data,
      );
      setSelected((s) => new Set([...s].filter((id) => !gone.has(id))));
    }
    setAssigningIds(new Set());
    queryClient.invalidateQueries(['cm-assignment']);
    queryClient.invalidateQueries(['client-onboarding']);
    if (done.length) {
      toast.success(
        on
          ? `${done.length === 1 ? '1 client is' : `${done.length} clients are`} marked for training.`
          : `${done.length === 1 ? '1 client is' : `${done.length} clients are`} back on Ready for a Claims Manager.`,
      );
    }
    failed.forEach((f) => toast.error(f));
  };

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    return () => root.classList.remove('dark');
  }, [theme]);
  const flipTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    try {
      localStorage.setItem(THEME_KEY, nextTheme);
    } catch {
      /* private mode: this session only */
    }
  };

  return (
    <PrivateLayout background={theme === 'dark' ? '#0E121B' : '#F5F7FA'} fluid>
      <div className="mx-auto w-full max-w-screen-2xl pb-10">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="mb-0 text-xl font-bold text-slate-800 dark:text-slate-100">CM Assignment</h2>
            <div className="mt-1.5 text-[12px] text-slate-500 dark:text-slate-400">
              Everyone who finished onboarding, waiting on their Claims Manager.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RecentMoves />
            <button
              type="button"
              onClick={flipTheme}
              aria-label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-500 outline-none shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/80 hover:bg-slate-50 focus:outline-none dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <section className={cn(CARD, 'min-w-0 overflow-hidden')}>
            {/* Title, search, and paging at the top right. */}
            <div
              className={cn(
                'flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-100 py-3 dark:border-white/10',
                PAD_X,
              )}
            >
              {/* Even quarters on a phone. One short word each (Nic
                  2026-10-30); the long name is the hover and the blurb. */}
              <div className="grid w-full min-w-0 grid-cols-4 items-center gap-1 sm:flex sm:w-auto" role="tablist" aria-label="Who to show">
                {[
                  { key: 'ready', label: 'Ready for a Claims Manager', short: 'Ready', n: waiting, icon: BadgeCheck, tone: 'text-emerald-500' },
                  { key: 'ptrs', label: 'Waiting on PTRs', short: 'PTRs', n: ptrs.waiting, icon: History, tone: 'text-pink-500' },
                  { key: 'one', label: 'Waiting on one item', short: 'Almost', n: one.waiting, icon: ListChecks, tone: 'text-violet-500' },
                  { key: 'training', label: 'For Training', short: 'Training', n: training.waiting, icon: GraduationCap, tone: 'text-sky-500' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    aria-label={t.label}
                    title={t.label}
                    onClick={() => setTab(t.key)}
                    className={cn(
                      'inline-flex h-8 min-w-0 items-center justify-center gap-2 rounded-full px-3 text-sm font-bold outline-none focus:outline-none sm:justify-start',
                      tab === t.key
                        ? 'bg-slate-900 text-white dark:!bg-white dark:!text-slate-900'
                        : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:!bg-white/10',
                    )}
                  >
                    <t.icon size={15} className={cn('shrink-0', tab === t.key ? '' : t.tone)} />
                    {/* Icon and count only on a phone (Nic 2026-11-03: the
                        word was cut to "R..."); the word from sm up. */}
                    <span className="hidden truncate sm:inline">{t.short}</span>
                    <span
                      className={cn(
                        'rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
                        tab === t.key ? 'bg-white/20 dark:!bg-slate-900/10' : 'bg-slate-900/[0.06] dark:!bg-white/10',
                      )}
                    >
                      {t.n}
                    </span>
                  </button>
                ))}
                {list.isFetching && !list.isLoading ? (
                  <Loader2 size={13} className="animate-spin text-slate-400" />
                ) : null}
              </div>
              <div className="ml-auto flex w-full items-center gap-2 sm:w-auto">
                <label className="relative mb-0 block min-w-0 flex-1 sm:w-56 sm:flex-none">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search name, email, phone"
                    className={cn(
                      'h-8 w-full rounded-full bg-white pl-8 pr-8 text-[13px] text-slate-700 outline-none dark:!bg-white/5 dark:text-slate-200',
                      query.trim()
                        ? 'shadow-[0_0_0_1.5px_rgba(14,165,233,0.85)]'
                        : 'ring-1 ring-slate-200 focus:ring-slate-300 dark:ring-white/10',
                    )}
                  />
                  {query ? (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label="Clear search"
                      className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-sky-600 outline-none hover:bg-sky-50 focus:outline-none dark:text-sky-300 dark:hover:!bg-sky-500/15"
                    >
                      <X size={13} />
                    </button>
                  ) : null}
                </label>
                <div className="flex shrink-0 items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                  <span className="whitespace-nowrap tabular-nums">
                    {total ? (
                      <>
                        <span className="font-semibold text-slate-700 dark:text-slate-200">
                          {from}-{to}
                        </span>{' '}
                        of {total}
                      </>
                    ) : (
                      '0'
                    )}
                  </span>
                  <PagerButton dir="prev" disabled={page <= 1} onClick={prev} />
                  <PagerButton dir="next" disabled={page >= pages} onClick={next} />
                </div>
              </div>
            </div>

            {/* Expedited people sitting in Waiting on PTRs are never missed. */}
            {tab === 'ready' && ptrs.expedited ? (
              <button
                type="button"
                onClick={() => setTab('ptrs')}
                className={cn(
                  'flex w-full items-center gap-2 border-b border-rose-100 bg-rose-50/60 py-2 text-left text-xs font-semibold text-rose-700 outline-none hover:bg-rose-50 focus:outline-none dark:border-rose-400/10 dark:!bg-rose-500/[0.06] dark:text-rose-300',
                  PAD_X,
                )}
              >
                <Zap size={13} /> {ptrs.expedited} expedited {ptrs.expedited === 1 ? 'is' : 'are'} waiting on PTRs. See them.
              </button>
            ) : null}
            {tab === 'ptrs' ? (
              <div className={cn('border-b border-slate-100 py-2 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400', PAD_X)}>
                Everything is in but their private treatment records. Assign one early only when needed: their claim
                is marked Waiting on documents: Private treatment records.
              </div>
            ) : null}
            {tab === 'one' ? (
              <div className={cn('border-b border-slate-100 py-2 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400', PAD_X)}>
                Everything is in but one item. Nothing to do here: their specialist ticks Got it and they move to
                Ready for a Claims Manager on their own.
              </div>
            ) : null}
            {tab === 'training' ? (
              <div className={cn('border-b border-slate-100 py-2 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400', PAD_X)}>
                Reserved for a Claims Manager in training. The picker lists only Claims Managers marked In Training
                on Internal Users; assigning works as usual.
              </div>
            ) : null}
            {/* The one picker and Assign for everyone ticked. On the For
                Training tab the picker lists trainees only (2026-10-30). */}
            {tab !== 'one' ? (
              <BulkAssignBar
                count={selected.size}
                cms={pickable}
                placeholder={tab === 'training' ? 'Pick a Claims Manager in training' : 'Pick a Claims Manager'}
                empty={tab === 'training' ? 'No Claims Manager is marked In Training. Mark one on Internal Users.' : null}
                secondary={
                  tab === 'ready'
                    ? { label: 'For training', icon: GraduationCap, onClick: () => markSelected(true), busy: mark.isLoading }
                    : tab === 'training'
                      ? { label: 'Back to Ready', icon: BadgeCheck, onClick: () => markSelected(false), busy: mark.isLoading }
                      : null
                }
                pick={pick}
                onPick={setPick}
                onAssign={assignSelected}
                assigning={assigningIds.size > 0}
                onClear={() => setSelected(new Set())}
              />
            ) : null}
            {/* Column header, md+ only (below it rows stack). */}
            {clients.length ? (
              <div
                className={cn(
                  'hidden grid-cols-1 gap-x-3 border-b border-slate-100 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400 dark:border-white/10 dark:text-slate-500 md:grid',
                  GRID,
                  PAD_X,
                )}
              >
                <div />
                <GridSortHead sortKey="name" label="Client" sort={sort} setSort={setSort} />
                <GridSortHead sortKey="rating" label="Rating" sort={sort} setSort={setSort} />
                <div>With</div>
                <GridSortHead sortKey="waiting" label={tab === 'one' ? 'Waiting on' : 'Waiting'} sort={sort} setSort={setSort} />
                <div />
              </div>
            ) : null}

            {list.isLoading ? (
              <div className="flex h-48 items-center justify-center">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : list.isError ? (
              <div className={cn('flex items-center gap-2 py-6 text-sm text-rose-600 dark:text-rose-300', PAD_X)}>
                <AlertTriangle size={16} />
                {list.error?.response?.data?.msg || "This list couldn't be loaded."}
              </div>
            ) : clients.length === 0 && q ? (
              <div className="flex flex-col items-center gap-1 py-14 text-center">
                <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">Nobody matches.</div>
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="text-xs font-semibold text-sky-600 outline-none hover:underline focus:outline-none dark:text-sky-400"
                >
                  Clear search
                </button>
              </div>
            ) : clients.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                  <Check size={20} />
                </span>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {tab === 'ptrs'
                    ? 'Nobody is waiting on PTRs.'
                    : tab === 'one'
                      ? 'Nobody is down to one item.'
                      : tab === 'training'
                        ? 'Nobody is marked for training.'
                        : 'Everyone is with a Claims Manager.'}
                </div>
                <div className="text-xs text-slate-400">
                  {tab === 'ptrs'
                    ? 'Clients whose checklist is only waiting on their private treatment records show here.'
                    : tab === 'one'
                      ? 'Clients whose checklist is waiting on exactly one item (other than PTRs alone) show here.'
                      : tab === 'training'
                        ? 'Tick clients on Ready and choose For training to reserve them for a Claims Manager in training.'
                        : 'Clients land here when their specialist marks them ready to be assigned.'}
                </div>
              </div>
            ) : (
              <>
                <ul
                  className={cn(
                    'mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/10',
                    list.isPreviousData && 'opacity-70',
                  )}
                >
                  {clients.map((c) => (
                    <ClientRow
                      key={c.onboardingId}
                      client={c}
                      cms={cms}
                      early={tab === 'ptrs'}
                      readOnly={tab === 'one'}
                      selected={selected.has(c.onboardingId)}
                      assigning={assigningIds.has(c.onboardingId)}
                      onToggle={() => toggle(c.onboardingId)}
                    />
                  ))}
                </ul>
                {pages > 1 ? (
                  <div
                    className={cn(
                      'flex items-center justify-end gap-1 border-t border-slate-100 py-2 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400',
                      PAD_X,
                    )}
                  >
                    <span className="mr-1 tabular-nums">
                      Page {page} of {pages}
                    </span>
                    <PagerButton dir="prev" disabled={page <= 1} onClick={prev} />
                    <PagerButton dir="next" disabled={page >= pages} onClick={next} />
                  </div>
                ) : null}
              </>
            )}
          </section>

          {/* On the For Training tab the rail compares trainees only. */}
          <CaseloadRail cms={pickable} trainingOnly={tab === 'training'} />
        </div>
      </div>
    </PrivateLayout>
  );
};

export default CmAssignmentPage;
